import { personRef } from "@/content/author";
import { createFileRoute, Link } from "@tanstack/react-router";
import { hreflangLinks } from "@/lib/seo";
import { PageIntro, PageSection, MoreLink } from "@/components/site/page";

const URL = "https://akillisurdurulebilirlik.com/yazilar/sdg-rehberi";
const TITLE = "SDGs (Sürdürülebilir Kalkınma Amaçları) — Eleştirel Bir Rehber";
const DESC =
  "SDGs nedir, 17 hedef neyi vaat ediyor, hangi noktada tıkanıyor? Sürdürülebilir Kalkınma Amaçlarının kısa tarihi, sayısal durumu ve teoriyle pratik arasındaki boşluğun eleştirel bir okuması.";
const PUBLISHED = "2026-06-18";

const FAQ = [
  {
    q: "SDGs nedir, kısaca?",
    a: "SDGs (Sustainable Development Goals — Sürdürülebilir Kalkınma Amaçları, kısaca SKH) BM'nin 2015'te kabul ettiği, 2030'a kadar ulaşılması hedeflenen 17 küresel hedeften oluşan bir çerçevedir. Yoksulluğun bitirilmesinden iklim eylemine, eşitsizlikten kurumsal yönetişime kadar geniş bir alanı kapsar ve 169 alt hedef, 230'dan fazla göstergeyle ölçülür.",
  },
  {
    q: "SDGs kaç hedeften oluşur?",
    a: "17 ana hedef ve 169 alt hedef vardır. Hedefler üç katmanda düşünülebilir: insan refahı (1–6: yoksulluk, açlık, sağlık, eğitim, cinsiyet eşitliği, su), ekonomi ve altyapı (7–12: enerji, iş, sanayi, eşitsizlik, şehirler, üretim) ve gezegen ile yönetişim (13–17: iklim, deniz, kara, barış, ortaklıklar).",
  },
  {
    q: "SDGs ile MDGs arasındaki fark nedir?",
    a: "MDGs (Binyıl Kalkınma Hedefleri, 2000–2015) sekiz hedeften oluşuyordu ve büyük ölçüde Küresel Güney'e yönelikti. SDGs ise 17 hedefle daha kapsamlı, evrensel (her ülke için geçerli) ve iklim, eşitsizlik, kurumlar gibi sistemik konuları doğrudan içeriyor. MDGs 'kalkınma yardımı' diliyle, SDGs ise 'ortak gelecek' diliyle yazılmıştır.",
  },
  {
    q: "SDGs 2030 hedeflerine ulaşılabilecek mi?",
    a: "BM'nin 2024 ilerleme raporuna göre hedeflerin yalnızca %17'si planlanan hızda ilerliyor, yarısından fazlası geride veya gerilemiş durumda. İklim (SDG 13), biyoçeşitlik (SDG 14–15) ve eşitsizlik (SDG 10) en yavaş ilerleyen başlıklar. 2030'a beş yıldan az kalmışken, mevcut hızla 17 hedefe ulaşmak matematiksel olarak mümkün görünmüyor.",
  },
  {
    q: "SDGs eleştirileri nelerdir?",
    a: "Üç ana eleştiri var: (1) Çelişki — sınırsız ekonomik büyüme (SDG 8) ile gezegen sınırları (SDG 13–15) aynı çerçevede uzlaştırılmaya çalışılır. (2) Ölçüm — 230 gösterge, devletlere kendi başarısını seçici şekilde raporlama imkânı verir; bu 'SDG washing' riskini doğurur. (3) Güç — hedefler 'ne' yapılacağını söyler ama 'kim' tarafından, 'kimin pahasına' sorusunu büyük ölçüde boş bırakır.",
  },
  {
    q: "Türkiye SDGs hedeflerinde nerede?",
    a: "Türkiye 2023 Gönüllü Ulusal İnceleme raporuna göre SDG sıralamasında dünyada 70'inci sıralarda yer alıyor. Eğitim (SDG 4) ve sağlık (SDG 3) görece güçlü; cinsiyet eşitliği (SDG 5), iklim eylemi (SDG 13) ve eşitsizlik (SDG 10) en zayıf başlıklar. SDG göstergeleri Türkiye İstatistik Kurumu (TÜİK) tarafından raporlanıyor.",
  },
  {
    q: "Yapay zekâ SDGs'e yardım eder mi?",
    a: "Hem evet hem hayır. AI, tahmin, optimizasyon ve veri toplamada hedeflerin 134'ünü hızlandırma potansiyeli taşıyor; ama aynı zamanda enerji tüketimi (SDG 7), istihdam dönüşümü (SDG 8) ve eşitsizlik (SDG 10) açılarından hedeflerden uzaklaşma riski yaratıyor. 'AI for Good' söyleminin sayılarla desteklenmediği her vaka, bir greenwashing varyantıdır.",
  },
];

export const Route = createFileRoute("/yazilar/sdg-rehberi")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      {
        name: "keywords",
        content:
          "sdgs, sdg, sürdürülebilir kalkınma amaçları, sürdürülebilir kalkınma hedefleri, skh, 17 sdg, BM SDG, sustainable development goals, sdg eleştirisi, sdg 2030, türkiye sdg, mdg sdg fark",
      },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [
      { rel: "canonical", href: URL },
      ...hreflangLinks("/yazilar/sdg-rehberi"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESC,
          inLanguage: "tr-TR",
          datePublished: PUBLISHED,
          dateModified: PUBLISHED,
          author: personRef(),
          publisher: {
            "@type": "Organization",
            name: "Akıllı Sürdürülebilirlik",
            url: "https://akillisurdurulebilirlik.com",
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": URL },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "https://akillisurdurulebilirlik.com/" },
            { "@type": "ListItem", position: 2, name: "Yazılar", item: "https://akillisurdurulebilirlik.com/yazilar" },
            { "@type": "ListItem", position: 3, name: "SDGs Rehberi", item: URL },
          ],
        }),
      },
    ],
  }),
  component: SdgRehberiPage,
});

function SdgRehberiPage() {
  return (
    <>
      <PageIntro
        crumbs={[{ label: "Yazılar", to: "/yazilar" }, { label: "SDGs Rehberi" }]}
        kicker="Eleştirel Rehber"
        title="SDGs: Teoride 17 hedef, pratikte ne kaldı?"
        lede={
          <>
            Sürdürülebilir Kalkınma Amaçları (SDGs) 2015'te bir uzlaşı belgesi
          olarak doğdu. On yıl sonra hedeflerin yalnızca yedide biri planlanan
          hızda ilerliyor. Bu rehber, SDGs'in ne olduğunu, neyi vaat ettiğini ve
          niye çoğu yerde sayılarla buluşmadığını anlatıyor.
          </>
        }
      />
      <PageSection>
        <article className="max-w-3xl">

      <nav className="mb-12 border border-ink/10 p-5 md:p-6 bg-secondary" aria-label="İçindekiler">
        <h2 className="text-[10px] uppercase tracking-[0.25em] font-bold text-ink/60 mb-3">
          İçindekiler
        </h2>
        <ol className="space-y-1.5 text-sm">
          <li><a href="#nedir" className="hover:text-theme-tech">1. SDGs nedir, kısaca</a></li>
          <li><a href="#tarih" className="hover:text-theme-tech">2. MDG'den SDG'ye: 2000–2015 dönüşümü</a></li>
          <li><a href="#17-hedef" className="hover:text-theme-tech">3. 17 hedef, üç katman</a></li>
          <li><a href="#durum" className="hover:text-theme-tech">4. 2030'a beş yıl: sayısal durum</a></li>
          <li><a href="#celiski" className="hover:text-theme-tech">5. SDG'nin içsel çelişkisi: büyüme vs sınır</a></li>
          <li><a href="#olcum" className="hover:text-theme-tech">6. Ölçüm sorunu ve SDG washing</a></li>
          <li><a href="#turkiye" className="hover:text-theme-tech">7. Türkiye'nin SDG karnesi</a></li>
          <li><a href="#ai" className="hover:text-theme-tech">8. Yapay zekâ ve SDG: çift uçlu kılıç</a></li>
          <li><a href="#ne-yapmali" className="hover:text-theme-tech">9. Ne yapmalı: 2030 sonrası</a></li>
          <li><a href="#sss" className="hover:text-theme-tech">10. Sıkça sorulanlar</a></li>
        </ol>
      </nav>

      <div className="prose-content space-y-6 text-base md:text-lg leading-[1.75] text-ink/85">
        <section id="nedir" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            1. SDGs nedir, kısaca
          </h2>
          <p>
            <strong>SDGs</strong> (Sustainable Development Goals — Türkçesiyle{" "}
            <strong>Sürdürülebilir Kalkınma Amaçları</strong> ya da kısaca SKH),
            Birleşmiş Milletler'in 25 Eylül 2015'te oybirliğiyle kabul ettiği{" "}
            <em>Agenda 2030</em> belgesinin omurgasıdır. 17 ana hedef, 169 alt
            hedef ve 230'dan fazla göstergeden oluşur. Hedeflere ulaşma tarihi:
            2030.
          </p>
          <p>
            Çerçevenin temel iddiası şudur: yoksulluğu bitirmek, eşitsizliği
            azaltmak ve iklimi korumak ayrı projeler değil, aynı sistemin iç
            içe geçmiş yüzleridir. Bir hedefte ilerleme, başka bir hedefte geri
            adım pahasına alınıyorsa gerçek bir ilerleme sayılmaz.
          </p>
        </section>

        <section id="tarih" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            2. MDG'den SDG'ye: 2000–2015 dönüşümü
          </h2>
          <p>
            SDGs'in atası, 2000'de imzalanan{" "}
            <strong>Binyıl Kalkınma Hedefleri (MDGs)</strong>'dir. MDGs sekiz
            hedeften oluşuyordu ve büyük ölçüde Küresel Güney'e dönüktü: aşırı
            yoksulluğun yarıya indirilmesi, çocuk ölümlerinin azaltılması,
            HIV/AIDS ile mücadele, ilköğretimin evrenselleştirilmesi.
          </p>
          <p>
            MDGs bazı başlıklarda büyük başarı sağladı — günde 1,25 dolar
            altında yaşayan nüfus 1990'da %36'dan 2015'te %12'ye düştü — ama
            iklim, eşitsizlik ve kurumsal yönetişim çerçeve dışındaydı. SDGs
            bu boşluğu kapatmak için tasarlandı: <strong>evrensel</strong> (her
            ülke için geçerli, sadece "gelişmekte olan ülkeler" için değil),{" "}
            <strong>sistemik</strong> (iklim, eşitsizlik ve kurumlar dahil) ve{" "}
            <strong>ortaklığa dayalı</strong> (SDG 17: devletler, sermaye ve
            sivil toplum birlikte).
          </p>
        </section>

        <section id="17-hedef" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            3. 17 hedef, üç katman
          </h2>
          <p>
            17 SDG hedefini üç katmanda okumak işi kolaylaştırır:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong>İnsan refahı (1–6):</strong> Yoksulluğun bitirilmesi,
              açlığın sonlandırılması, sağlık, kaliteli eğitim, cinsiyet
              eşitliği ve temiz suya erişim. Bireyin yaşamını doğrudan
              tanımlayan hedefler.
            </li>
            <li>
              <strong>Ekonomi ve altyapı (7–12):</strong> Erişilebilir enerji,
              insana yaraşır iş, sanayi ve inovasyon, eşitsizliklerin
              azaltılması, sürdürülebilir şehirler ve sorumlu üretim-tüketim.
              Üretim biçimini ve dağılımı düzenlemeye çalışan hedefler.
            </li>
            <li>
              <strong>Gezegen ve yönetişim (13–17):</strong> İklim eylemi, sudaki
              ve karadaki yaşam, barış ve güçlü kurumlar, küresel ortaklıklar.
              Hedeflerin oturduğu zemini koruyan hedefler.
            </li>
          </ul>
          <p>
            17 numaralı hedef — <em>küresel ortaklıklar</em> — diğer 16'sının
            mümkün olup olmamasını belirleyen meta hedef. Sermaye akışı,
            teknoloji transferi ve veri paylaşımı olmadan diğer 16 hedef
            kâğıt üzerinde kalır.
          </p>
        </section>

        <section id="durum" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            4. 2030'a beş yıl: sayısal durum
          </h2>
          <p>
            BM'nin 2024 SDG Raporu'nun en çarpıcı verisi şu: 169 alt hedefin{" "}
            <strong>yalnızca %17'si</strong> planlanan hızda ilerliyor.
            %48'i geride, %18'i ise 2015'teki başlangıç noktasından daha
            kötü durumda. Pandemi, savaşlar ve enflasyon ilerlemeyi yavaşlattı
            ama sebep onlardan çok daha eskiye uzanıyor: hedefler arasındaki
            kaynak rekabeti hiçbir zaman çözülmedi.
          </p>
          <p>
            Hangi hedeflerde en geride kaldık? SDG 2 (Sıfır açlık) — küresel
            açlık 2015'ten beri artıyor. SDG 13 (İklim eylemi) — 2030 emisyon
            hedeflerinin gerisindeyiz. SDG 14–15 (Sudaki ve karadaki yaşam) —
            biyoçeşitlik kaybı hızlanıyor. SDG 10 (Eşitsizliklerin azaltılması) —
            en zengin %1, son on yılda küresel servetin %38'ini aldı.
          </p>
        </section>

        <section id="celiski" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            5. SDG'nin içsel çelişkisi: büyüme vs sınır
          </h2>
          <p>
            SDG 8 "sürdürülebilir ekonomik büyüme"yi açıkça hedefler — en az
            yıllık %7 GSYH artışı (en az gelişmiş ülkeler için). SDG 13 ise
            iklim sistemini tehdit eden emisyonların kesilmesini ister.
          </p>
          <p>
            Bu iki hedef matematiksel olarak gerilim içindedir. Tarihsel olarak
            ekonomik büyüme ile karbon emisyonu birbirinden{" "}
            <em>kesin biçimde ayrışmış</em> (decoupling) hiçbir ekonomide
            görülmedi. Verimlilik kazançları emisyonun yoğunluğunu düşürür,
            ama toplam emisyonu artırmaya devam eder — bu klasik{" "}
            <strong>Jevons paradoksu</strong>'dur.
          </p>
          <p>
            SDG çerçevesi bu gerilimi açıkça tartışmaz. "Yeşil büyüme" varsayımı
            altına saklar. Eleştirmenler — özellikle{" "}
            <em>post-growth</em> ve <em>degrowth</em> literatürü — bu
            varsayımın matematiğini sorgular ve hedefin baştan yeniden
            yazılması gerektiğini söyler.
          </p>
        </section>

        <section id="olcum" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            6. Ölçüm sorunu ve SDG washing
          </h2>
          <p>
            230 gösterge çok güçlü görünür. Pratikte ise üç sorunu birden
            yaratır:
          </p>
          <ol className="list-decimal pl-6 space-y-3">
            <li>
              <strong>Veri boşluğu:</strong> Düşük gelirli ülkelerin
              yarısından fazlasında göstergelerin yarısı için güncel veri yok.
              Olmayan veri, başarı ya da başarısızlığı görünmez kılar.
            </li>
            <li>
              <strong>Seçici raporlama:</strong> Hükümetler ve şirketler iyi
              performans gösterdikleri göstergeleri öne çıkarır, zayıf
              olanları sessizce bırakır. SDG iletişimi vitrin tasarımına döner.
            </li>
            <li>
              <strong>SDG washing:</strong> Bir şirket sürdürülebilirlik
              raporunda 17 renkli ikonu yan yana koyar, ama operasyonunda
              hangi hedefe nasıl katkı yaptığını sayılarla göstermez.
              Greenwashing'in kurumsal akrabası.
            </li>
          </ol>
          <p>
            SDG göstergesi okumak, sayıya değil <em>sayının arkasındaki
            metodolojiye</em> bakmayı gerektirir — tıpkı{" "}
            <Link to="/yazilar/surdurulebilirlik-nedir-rehberi" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              sürdürülebilirlik tanımında
            </Link>{" "}
            olduğu gibi.
          </p>
        </section>

        <section id="turkiye" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            7. Türkiye'nin SDG karnesi
          </h2>
          <p>
            Türkiye, 2023 Gönüllü Ulusal İnceleme (VNR) raporuna göre küresel
            SDG sıralamasında 70'inci sıralarda. Güçlü olduğu alanlar SDG 4
            (Eğitim — okullaşma oranları yüksek) ve SDG 3 (Sağlık —
            kapsayıcı sağlık sistemi). Zayıf olduğu alanlar ise SDG 5
            (Cinsiyet eşitliği — işgücüne katılım %35 civarında), SDG 13
            (İklim eylemi — Paris Anlaşması'na 2021'de geç katıldı) ve SDG 10
            (Eşitsizlik — Gini katsayısı OECD ortalamasının üstünde).
          </p>
          <p>
            TÜİK SDG göstergelerini düzenli olarak raporlar; veri seti
            kamuya açıktır. Ama ulusal raporlama ile bağımsız değerlendirmeler
            (örneğin Sustainable Development Report) arasında bazı
            göstergelerde 10–15 puanlık farklar görülür — metodoloji
            tartışmasının yerel yansıması.
          </p>
        </section>

        <section id="ai" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            8. Yapay zekâ ve SDG: çift uçlu kılıç
          </h2>
          <p>
            <em>Nature</em>'da 2020'de yayımlanan çığır açıcı bir çalışma,
            yapay zekânın 169 alt hedefin <strong>134'ünü hızlandırabileceğini</strong>{" "}
            ama aynı zamanda 59'una zarar verebileceğini gösterdi. Yardım
            edebileceği yerler: tarımda verim tahmini (SDG 2), hastalık
            teşhisi (SDG 3), enerji şebekesi optimizasyonu (SDG 7), iklim
            modellemesi (SDG 13).
          </p>
          <p>
            Zarar verebileceği yerler: büyük model eğitiminin enerji yükü
            (SDG 7'yi geri çekiyor), istihdam dönüşümü (SDG 8 ile gerilim),
            algoritmik ayrımcılık (SDG 5 ve 10'u tehdit ediyor), veri
            merkezlerinin su tüketimi (SDG 6).
          </p>
          <p>
            "AI for SDG" söyleminin sayılarla desteklenmediği her vaka,{" "}
            <strong>SDG washing</strong>'in dijital varyantıdır. Doğru soru
            şudur: <em>hangi modeli, hangi hedef için, hangi enerji karmasıyla
            çalıştırıyoruz</em>? Daha kapsamlı bir okuma için:{" "}
            <Link to="/yapay-zeka-toplum" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
              Yapay Zekâ ve Toplum
            </Link>.
          </p>
        </section>

        <section id="ne-yapmali" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            9. Ne yapmalı: 2030 sonrası
          </h2>
          <p>
            2030'a beş yıldan az kalmışken üç yön tartışılıyor:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong>Hedefleri ertelemek (post-2030):</strong> Mevcut 17
              hedefi takvimle uzatmak. Eleştirmenler "başarısızlığı
              kurumsallaştırır" diyor.
            </li>
            <li>
              <strong>Hedefleri sadeleştirmek:</strong> 169 alt hedefi 30–40
              çekirdek göstergeye indirgemek; ölçülemeyenleri çıkarmak.
              Pratik ama derinliği azaltma riski var.
            </li>
            <li>
              <strong>Çerçeveyi yeniden yazmak:</strong> "Büyüme" varsayımını
              gevşetmek, gezegen sınırlarını (planetary boundaries) merkeze
              koymak. <em>Doughnut economics</em> bu yönde en güçlü öneri.
            </li>
          </ul>
          <p>
            Bireysel ve kurumsal düzeyde — hangi yol seçilirse seçilsin — üç
            şey değişmeden anlamlı ilerleme zor: <strong>şeffaf veri</strong>,{" "}
            <strong>bağımsız doğrulama</strong> ve <strong>hedefler arası
            denkleştirmeyi açıkça gösteren raporlama</strong>. Bir hedefte
            kazanılanın hangi hedefte kaybedildiğini görmeden, ilerlemeyi
            ölçemeyiz.
          </p>
        </section>

        <section id="sss" className="scroll-mt-24">
          <h2 className="font-serif text-2xl md:text-3xl tracking-tight mt-12 mb-4 text-ink">
            10. Sıkça sorulanlar
          </h2>
          <div className="space-y-6 mt-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-serif text-lg md:text-xl text-ink mb-2">{f.q}</h3>
                <p className="text-ink/80">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-16 border-t border-ink/10 pt-10">
          <h2 className="font-serif text-2xl tracking-tight mb-4 text-ink">Devamı için</h2>
          <ul className="space-y-3 text-base">
            <li>
              →{" "}
              <Link to="/yazilar/surdurulebilirlik-nedir-rehberi" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                Sürdürülebilirlik Nedir?
              </Link>{" "}
              — kavramın tanımı, tarihi ve üç sütun modeli.
            </li>
            <li>
              →{" "}
              <Link to="/yazilar/greenwashing-nedir" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                Greenwashing Nedir?
              </Link>{" "}
              — SDG washing'in büyük kardeşi.
            </li>
            <li>
              →{" "}
              <Link to="/yazilar/yesil-mutabakat-rehberi" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                Avrupa Yeşil Mutabakatı Rehberi
              </Link>{" "}
              — yeşil büyüme çelişkisinin AB hukukundaki hali.
            </li>
            <li>
              →{" "}
              <Link to="/iklim-sistemleri" className="underline decoration-theme-tech underline-offset-4 hover:text-theme-tech">
                İklim Sistemleri
              </Link>{" "}
              — SDG 13'ün arkasındaki gezegen ölçeği.
            </li>
          </ul>
        </section>
      </div>
        </article>
      </PageSection>
    </>
  );
}
